import React, { useState, useEffect } from 'react';
import { DIALOG_TITLE_SX } from '../utils/dialogStyle';
import {
    Box, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography,
    TextField, InputAdornment, Button, CircularProgress, Chip, Alert, Paper,
} from '@mui/material';
import { Close, Search as SearchIcon, CheckCircle, PhotoCamera, ArrowBack } from '@mui/icons-material';
import { businessAPI } from '../api';

/**
 * CataloguePicker — vendor picks master (catalogue) products in bulk, then sets a
 * price for each on one screen. Used by Orders / Both businesses.
 *   Step 0: choose (multi-select, with photos)
 *   Step 1: set prices → bulk-add
 */
const CataloguePicker = ({ open, onClose, businessId, existingProductIds = [], onAdded }) => {
    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState({}); // { [f_id]: { product, price } }
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        setStep(0); setSearch(''); setSelected({}); setError('');
        (async () => {
            setLoading(true);
            try {
                const res = await businessAPI.listProducts(null);
                setProducts(res.data || []);
            } catch {
                setError('Failed to load catalogue');
            } finally {
                setLoading(false);
            }
        })();
    }, [open, businessId]);

    const addedSet = new Set(existingProductIds);

    const toggle = (p) => {
        setSelected(prev => {
            const next = { ...prev };
            if (next[p.f_id]) delete next[p.f_id];
            else next[p.f_id] = { product: p, price: p.f_base_price != null ? String(p.f_base_price) : '' };
            return next;
        });
    };
    const setPrice = (id, price) => setSelected(prev => ({ ...prev, [id]: { ...prev[id], price } }));

    const chosen = Object.values(selected);
    const visible = products.filter(p =>
        !search.trim() || p.f_name?.toLowerCase().includes(search.trim().toLowerCase())
    );
    const canSave = chosen.length > 0 && chosen.every(c => c.price && parseFloat(c.price) > 0);

    const handleSave = async () => {
        setSaving(true); setError('');
        try {
            const items = chosen.map(c => ({ f_product_id: c.product.f_id, initial_price: parseFloat(c.price) }));
            const res = await businessAPI.bulkAddProducts({ f_business_id: businessId, items });
            if (onAdded) onAdded(res.data);
            onClose();
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to add products');
        } finally {
            setSaving(false);
        }
    };

    const thumb = (p) => (
        <Box sx={{ width: 44, height: 44, borderRadius: 1.5, bgcolor: 'grey.100', flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {p.images?.[0]
                ? <Box component="img" src={p.images[0]} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <PhotoCamera sx={{ color: 'text.disabled', fontSize: 18 }} />}
        </Box>
    );

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" scroll="paper"
            PaperProps={{ sx: { borderRadius: 2, height: '90vh' } }}>
            <DialogTitle sx={{ ...DIALOG_TITLE_SX, display: 'flex', alignItems: 'center', gap: 1 }}>
                {step === 1 && (
                    <IconButton size="small" onClick={() => setStep(0)}><ArrowBack fontSize="small" /></IconButton>
                )}
                <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>
                    {step === 0 ? 'Add from list' : 'Set prices'}
                </Typography>
                <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 1.5 }}>
                {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}

                {/* ── Step 0: choose ── */}
                {step === 0 && (
                    <>
                        <TextField size="small" fullWidth placeholder="Search products…"
                            value={search} onChange={e => setSearch(e.target.value)} sx={{ mb: 1.5 }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                        />
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                        ) : visible.length === 0 ? (
                            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>No products found</Typography>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {visible.map(p => {
                                    const already = addedSet.has(p.f_id);
                                    const isSel = !!selected[p.f_id];
                                    return (
                                        <Paper key={p.f_id} variant="outlined"
                                            onClick={() => !already && toggle(p)}
                                            sx={{
                                                display: 'flex', alignItems: 'center', gap: 1.25, p: 1, borderRadius: 2,
                                                cursor: already ? 'default' : 'pointer',
                                                opacity: already ? 0.55 : 1,
                                                borderColor: isSel ? 'primary.main' : 'divider',
                                                borderWidth: isSel ? 1.5 : 1,
                                                bgcolor: isSel ? 'primary.50' : 'background.paper',
                                            }}>
                                            {thumb(p)}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" fontWeight={600} noWrap>{p.f_name}</Typography>
                                                {p.f_base_price != null && (
                                                    <Typography variant="caption" color="text.secondary">₹{p.f_base_price} suggested</Typography>
                                                )}
                                            </Box>
                                            {already
                                                ? <Chip size="small" label="Added" color="success" sx={{ height: 22, fontSize: '0.7rem' }} />
                                                : <CheckCircle sx={{ color: isSel ? 'primary.main' : 'grey.300', fontSize: 22 }} />}
                                        </Paper>
                                    );
                                })}
                            </Box>
                        )}
                    </>
                )}

                {/* ── Step 1: set prices ── */}
                {step === 1 && (
                    chosen.length === 0 ? (
                        <Typography color="text.secondary" align="center" sx={{ py: 4 }}>Nothing selected</Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {chosen.map(({ product: p, price }) => (
                                <Paper key={p.f_id} variant="outlined" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: 1, borderRadius: 2 }}>
                                    {thumb(p)}
                                    <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0 }} noWrap>{p.f_name}</Typography>
                                    <TextField size="small" type="number" label="Your price" value={price}
                                        onChange={e => setPrice(p.f_id, e.target.value)}
                                        sx={{ width: 120, flexShrink: 0 }}
                                        inputProps={{ step: 0.01, inputMode: 'decimal' }}
                                        InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                    />
                                </Paper>
                            ))}
                        </Box>
                    )
                )}
            </DialogContent>

            <DialogActions sx={{ px: 2, pb: 1.5 }}>
                {step === 0 ? (
                    <Button fullWidth variant="contained" disabled={chosen.length === 0} onClick={() => setStep(1)}
                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 1 }}>
                        Set prices ({chosen.length})
                    </Button>
                ) : (
                    <Button fullWidth variant="contained" disabled={!canSave || saving} onClick={handleSave}
                        sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 1 }}>
                        {saving ? <CircularProgress size={20} color="inherit" /> : `Save ${chosen.length} product${chosen.length > 1 ? 's' : ''}`}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CataloguePicker;
